import { Locale } from '../ruleset/locale';
import playerNames from '../data/PlayerNames.json';

/**
 * Return index of weighted random
 *
 * @example
 *
 * const damages = [
 *  { chance: 50, damage: 1 },
 *  { chance: 25, damage: 10 },
 *  { chance: 25, damage: 20 },
 * ];
 *
 * const weights = damages.map(function (item) {
 *   return item.chance;
 * });
 *
 * const index = weightedRandom(weights);
 * const result = damages[index].damage;
 *
 * @param weights available weights
 * @param random
 */
export function weightedRandom(
  weights: number[],
  random: () => number,
): number {
  let sum = 0;

  for (let i = 0; i < weights.length; i++) {
    sum += weights[i];
  }

  let roll = random() * sum;

  for (let i = 0; i < weights.length; i++) {
    if (roll < weights[i]) {
      return i;
    }

    roll -= weights[i];
  }

  throw new Error('weightedRandom get empty weights array');
}

/**
 * Fisher–Yates shuffle. Any other algorithm is not correct. This is critical!
 * Returns void to remind the user that this mutates the original array.
 */
export function shuffleArray<T>(a: Array<T>, random: () => number): void {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const x = a[i];
    a[i] = a[j];
    a[j] = x;
  }
}

export const getRandomFloat = (
  min: number,
  max: number,
  random: () => number,
) => {
  return min + random() * (max - min);
};

export const getRandomInt = (
  min: number,
  max: number,
  random: () => number,
) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(random() * (max - min + 1)) + min;
};

export const getRandomItemsFromArr = <T>(
  arr: T[],
  n: number,
  random: () => number,
) => {
  const shuffled = arr.slice();

  shuffleArray(shuffled, random);

  return shuffled.slice(0, n);
};

export const getRandomItemFromArray = <T>(arr: T[], random: () => number) => {
  return arr[getRandomInt(0, arr.length - 1, random)];
};

export function randomString(length: number, random: () => number) {
  const chars =
    '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let result = '';

  for (let i = length; i > 0; --i) {
    result += chars[Math.floor(random() * chars.length)];
  }

  return result;
}

export function generateRandomName(lang: Locale, random: () => number): string {
  const names = [...playerNames];
  const result = names.reduce(
    (result, current) => {
      const key = lang === 'en' ? 'name_en' : 'name_jp';
      const name = current[key];

      if (current.part === 'first') {
        result.first.push(name);
      } else {
        result.last.push(name);
      }

      return result;
    },
    {
      first: [] as string[],
      last: [] as string[],
    },
  );

  const first = getRandomItemFromArray(result.first, random);
  const last = getRandomItemFromArray(result.last, random);

  return `${first}${last}`;
}

export function getRandomBoolean(chance: number, random: () => number) {
  while (chance > 1) {
    chance /= 100;
  }
  return random() < chance;
}
